'use client';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  CloseDateAndCntDto,
  PetrohqApiResponseListCloseDateAndCntDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { ko } from 'date-fns/locale';
import { CalendarIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

/** yyyyMMdd → Date */
const toDate = (value: string) =>
  value.length === 8
    ? new Date(
        Number(value.slice(0, 4)),
        Number(value.slice(4, 6)) - 1,
        Number(value.slice(6, 8))
      )
    : undefined;

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * 마감일자·마감차수 필터.
 *
 * 선택 가능한 날짜와 차수가 주유소별 마감 이력에서 나오므로 이 섹션이 직접 조회한다.
 * 주유소가 바뀌면 목록을 다시 받고 최신 일자·마지막 차수로 되잡는다 - 이전 주유소의
 * 마감일자가 남아 있으면 조회가 빈 결과로 떨어진다.
 */
export function CloseDateSection() {
  const { toast } = useToast();
  const store = useFilterStore(state => state.store);
  const closeDate = useFilterStore(state => state.closeDate);
  const closeCount = useFilterStore(state => state.closeCount);
  const updateFilter = useFilterStore(state => state.updateFilter);

  const [closeDates, setCloseDates] = useState<CloseDateAndCntDto[]>([]);

  const countsOf = (date: string, list: CloseDateAndCntDto[]) => {
    const found = list.find(item => item.closedt === date);
    return found?.closecnt
      ? Array.from({ length: parseInt(found.closecnt) }, (_, i) =>
          (i + 1).toString()
        )
      : [];
  };

  const availableCounts = countsOf(closeDate, closeDates);

  const selectDate = useCallback(
    (date: string, list: CloseDateAndCntDto[]) => {
      updateFilter('closeDate', date);
      const counts = countsOf(date, list);
      updateFilter('closeCount', counts.length ? counts[counts.length - 1] : '');
    },
    [updateFilter]
  );

  useEffect(() => {
    if (store.length !== 1) {
      setCloseDates([]);
      updateFilter('closeDate', '');
      updateFilter('closeCount', '');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await apiClient.get(
          `close/stores/${store[0]}/close-dates`
        );
        const data =
          (await response.json()) as PetrohqApiResponseListCloseDateAndCntDto;
        if (cancelled) return;

        const list = data.result ?? [];
        setCloseDates(list);
        const latest = list.reduce<string>(
          (acc, cur) => ((cur.closedt || '') > acc ? cur.closedt || '' : acc),
          ''
        );
        selectDate(latest, list);
      } catch (error) {
        if (cancelled) return;
        console.error('Close dates fetch error:', error);
        toast({
          title: '오류',
          description: '마감일자 조회 중 오류가 발생했습니다.',
          variant: 'destructive',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [store, selectDate, updateFilter, toast]);

  if (store.length !== 1) {
    return (
      <div className='text-xs text-muted-foreground'>주유소를 1곳 선택하세요</div>
    );
  }

  const selectedDate = toDate(closeDate);
  const availableDates = closeDates
    .map(item => item.closedt)
    .filter((value): value is string => !!value && value.length === 8)
    .map(value => toDate(value)!);

  const controls = (
    <div className='flex items-center gap-1'>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            size='sm'
            className='font-normal'
            disabled={closeDates.length === 0}
          >
            <CalendarIcon className='mr-2 h-4 w-4' />
            {selectedDate
              ? `${closeDate.slice(0, 4)}-${closeDate.slice(4, 6)}-${closeDate.slice(6, 8)}`
              : '마감일자 선택'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                const year = date.getFullYear();
                const month = `${date.getMonth() + 1}`.padStart(2, '0');
                const day = `${date.getDate()}`.padStart(2, '0');
                selectDate(`${year}${month}${day}`, closeDates);
              }
            }}
            disabled={date => !availableDates.some(d => isSameDay(d, date))}
            locale={ko}
            showOutsideDays={false}
            className='rounded-md border'
          />
        </PopoverContent>
      </Popover>

      <Select
        value={closeCount}
        onValueChange={value => updateFilter('closeCount', value)}
        disabled={availableCounts.length === 0}
      >
        <SelectTrigger className='h-8 w-[100px] text-xs'>
          <SelectValue placeholder='차수' />
        </SelectTrigger>
        <SelectContent>
          {availableCounts.map(count => (
            <SelectItem key={count} value={count}>
              {count === 'All' ? '일마감' : `${count}차`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  if (closeDates.length > 0) {
    return controls;
  }

  // 비활성 컨트롤은 포인터 이벤트를 삼켜 툴팁이 안 뜬다 - 어차피 누를 수 없으니 통과시킨다
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex [&_button]:pointer-events-none'>
            {controls}
          </span>
        </TooltipTrigger>
        <TooltipContent>마감정보가 없습니다</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
